/**
 * 结算服务类型定义
 * 基于 billing_service_frontend_scheduler_plan.md 方案实现
 */

import { BillTypeEnum, BillStatusEnum, PageResponseType } from './CommonTypes';

// 调账任务状态枚举
export enum AdjustmentTaskStatusEnum {
  INITIATED = 'INITIATED',
  COMPLETED = 'COMPLETED'
}


// 账单重跑任务状态
export enum BillRerunTaskStatus {
  PENDING = 0,
  RUNNING = 1,
  SUCCESS = 2,
  FAILED = 3
}


// 账单类型
export interface SettlementBillType {
  id: string;
  billingPeriodInstanceId?: string;
  clientName: string;
  clientId: string;
  channelId: string;
  billType: BillTypeEnum;
  status: BillStatusEnum;
  billAmount: number;
  orderCount?: number;
  periodStart: string;
  periodEnd: string;
  timeZone: string;
  createdAt: string;
  updatedAt: string;
  isCanceled: boolean;
  isSent: boolean;
  lastSentAt: string | null;
  canRerun: boolean;
  currency: string;
  /** 发票状态：NOT_ISSUED/ISSUED_NOT_SENT/SENT；null 视为未开具 */
  invoiceStatus?: string | null;
  /**
   * 账期是否在 DSP Invoice 线上化切换点之后。
   * false 表示该账单走线下流程，不展示 Invoice 入口与 Invoice/付款操作按钮。
   * 未下发该字段的查询留空，调用方须显式判断而非默认放行。
   */
  invoiceEligible?: boolean;
  /**
   * DSP Invoice 是否已提交给财务：账单状态 >= EXTERNAL_CONFIRMED 且 invoice_ids 非空。
   * 仅 DSP 账单列表下发该字段，收入侧账单不适用。
   */
  invoiceSubmitted?: boolean;
  /** 财务驳回 DSP Invoice 的当前有效原因；DSP 重新提交后为 null。仅 DSP 账单详情下发 */
  rejectReason?: string | null;
  /**
   * DSP 公司全称（dsp.full_name），即 Invoice 上的对外名称。
   * 仅 getBillListing 下发；非 DSP 账单或 DSP 未维护全称时为 null，展示时需降级为 '-'。
   */
  dspFullName?: string | null;
  /** DSP Invoice 金额（单位：厘）。仅 DSP 账单下发；未提交 Invoice 时为 null */
  invoiceAmount?: number | null;
  /** Invoice 币种，用于金额格式化 */
  invoiceCurrency?: string | null;
  /** Invoice 金额与账单结算金额是否不一致；null 表示无 Invoice 或未计算 */
  invoiceAmountMismatch?: boolean | null;
  /** 账单节奏：1=白名单（快节奏），2=普通 */
  settlementCadence?: number | null;
  __typename?: string;
}

// 账单费用汇总类型
export interface BillFeeSummaryType {
  feeItemId: string;
  feeItemName: string;
  totalAmount: number;
  amountSign: number;
  allowAdjustment: boolean;
  __typename?: string;
}

// 调账记录类型
export interface AdjustmentRecordType {
  taskId: string;
  adjustmentType: string;
  adjustmentAmount: string;
  amountBefore: string;
  amountAfter: string;
  feeItemName: string;
  reason: string;
  submitterId: string;
  submitterName: string;
  submittedAt: string;
  status: string;
  __typename?: string;
}


// 分页请求类型 - 使用统一的 PageRequestType
export interface PageRequestType {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

// 账单查询过滤条件
export interface BillFiltersInput {
  billId?: string;
  clientId?: string;
  /** 客户ID列表（多选）；非空时优先于 clientId */
  clientIds?: string[];
  clientNameKeyword?: string;
  billType?: string;
  /** 账单类型列表（多选，与 billType 二选一）。客户账单页用于一次筛选收入侧全部类型 */
  billTypes?: string[];
  billStatus?: 'INTERNAL_ALREADY' | 'ADJUSTMENT_PENDING' | 'INTERNAL_CONFIRMED' | 'EXTERNAL_REJECTED' | 'EXTERNAL_CONFIRMED';
  billTimeRange?: BillTimeRangeInput;
  billAmountRange?: BillAmountRangeInput;
  /** 是否已发送；不传表示不限 */
  isSent?: boolean;
  /** 发票状态筛选：NOT_ISSUED/ISSUED_NOT_SENT/SENT；不传表示不限 */
  invoiceStatus?: string;
  /** DSP Invoice 提交状态筛选：true=已提交，false=未提交（已提交条件的取反）；不传表示不限 */
  invoiceSubmitted?: boolean;
  includeCanceled?: boolean;
  includeDeleted?: boolean;
}

// 账单时间范围输入
export interface BillTimeRangeInput {
  startTime?: string;
  endTime?: string;
  timeZoneId?: string;
  timeFormat?: string;
}

// 账单金额范围输入（与 GraphQL BillAmountRangeInput）
export interface BillAmountRangeInput {
  minAmount?: string | null;
  maxAmount?: string | null;
  currency?: string;
}

// 调账请求输入
export interface AdjustmentRequestInput {
  settlementFeeItemId: string;
  adjustmentAmount: string;
  reason: string;
}

// 批量调账请求输入
export interface BatchAdjustmentRequestInput {
  adjustments: AdjustmentRequestInput[];
}

// 账单重跑请求输入
export interface BillRerunRequestInput {
  clientId: string;
  periodInstanceId: string;
  reason: string;
}

// 批量账单重跑请求输入
export interface BatchBillRerunRequestInput {
  rerunRequests: BillRerunRequestInput[];
}


// 账单列表响应
export interface SettlementBillResponseType {
  content: SettlementBillType[];
  pageInfo: PageResponseType;
  summary: SettlementBillListingSummaryType;
}

// 账单列表汇总信息
export interface SettlementBillListingSummaryType {
  customerCount: number;
  billCount: number;
  totalAmount: string;
  __typename?: string;
}

// 账单详情响应
export interface SettlementBillDetailResponseType {
  bill: SettlementBillType;
  feeSummaries: BillFeeSummaryType[];
  adjustmentRecords: AdjustmentRecordType[];
}

// 调账响应
export interface AdjustmentResponseType {
  taskId: string;
  success: boolean;
  errorMessage?: string;
}

// 批量调账响应
export interface BatchAdjustmentResponseType {
  successCount: number;
  failureCount: number;
  failures: AdjustmentFailureDetail[];
}

// 调账失败详情
export interface AdjustmentFailureDetail {
  feeItemId: string;
  reason: string;
}

// 账单重跑响应
export interface BillRerunResponseType {
  taskId: string;
  success: boolean;
  errorMessage?: string;
}

// 批量账单重跑响应
export interface BatchBillRerunResponseType {
  successCount: number;
  failureCount: number;
  failures: BillRerunFailureDetail[];
}

// 账单重跑失败详情
export interface BillRerunFailureDetail {
  clientId: string;
  periodInstanceId: string;
  reason: string;
}



// ============= 账单汇总异步任务类型 =============

// 汇总任务状态枚举
export enum BillSummaryTaskStatus {
  PENDING = 0,     // 待处理
  RUNNING = 1,     // 处理中
  SUCCESS = 2,     // 成功
  FAILED = 3       // 失败
}

// 账单重跑进度
export interface BillRerunProgress {
  taskId: string;
  status: number;
  progress: number;
  currentStep?: string;
  periodInstanceId: string;
  clientId: string;
  clientName?: string;
  billType: string;
  periodStart: string;
  periodEnd: string;
  timeZone: string;
  reason: string;
  originalBillId?: string;
  newBillId?: string;
  errorMessage?: string;
  createdAt: string;
  completedAt?: string;
}

// 汇总任务创建结果
export interface BillSummaryTaskCreateResult {
  taskId: string;
  success: boolean;
  message: string;
}

// 汇总任务进度
export interface BillSummaryProgress {
  taskId: string;
  status: number; // 0=待处理，1=处理中，2=成功，3=失败
  progress: number; // 0-100
  currentStep?: string;
  summaryCount?: number;
  totalAmount?: number;
  errorMessage?: string;
}

// 汇总任务历史记录
export interface BillSummaryHistory {
  id: string;
  billId: string;
  operatorName: string;
  status: number;
  progress: number;
  currentStep?: string;
  summaryCount?: number;
  totalAmount?: number;
  createdAt: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface BillStatusTransitionRecord {
  id: string;
  billId: string;
  fromStatus?: string | null;
  fromStatusLabel?: string | null;
  toStatus: string;
  toStatusLabel: string;
  operatorId?: string | null;
  operatorName?: string | null;
  remark?: string | null;
  attachmentObjectKey?: string | null;
  attachmentDownloadUrl?: string | null;
  createdAt: string;
}

